import React, { useState } from "react";
import { ChevronDown, Pencil, Trash2, Check, X, RotateCcw, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { useConcierge } from "../../lib/concierge";

const LABELS = {
  region: ["Region", "Regiune"], start_city: ["Start", "Plecare"], end_city: ["End", "Întoarcere"], travel_period: ["When", "Perioada"],
  duration_days: ["Days", "Zile"], nights: ["Nights", "Nopți"], group_size: ["People", "Persoane"],
  travelers: ["Travelers", "Călători"], transport: ["Transport", "Transport"],
  required_destinations: ["Must-see", "Obligatoriu"], pace: ["Pace", "Ritm"],
  mobility: ["Mobility needs", "Nevoi de mobilitate"], driving_restrictions: ["Driving limits", "Limite condus"],
  accommodation: ["Stay", "Cazare"], budget_alternatives: ["Budgets", "Bugete"], decisions: ["Decided", "Decis"],
};
const ORDER = Object.keys(LABELS);
const STATUS = {
  stated: ["you said", "ai spus"], confirmed: ["confirmed", "confirmat"],
  assumption: ["assumption", "presupunere"], derived: ["calculated", "calculat"],
};

export const fmtValue = (v) => {
  if (v == null) return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object" && "min" in v) return v.min === v.max ? `${v.min}` : `${v.min}–${v.max}`;
  return String(v);
};

function FactRow({ field, fact, ro }) {
  const { editField } = useConcierge();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [bad, setBad] = useState(false);
  const save = async () => {
    const ok = await editField(field, val, "set");
    setBad(!ok);
    if (ok) setEditing(false);
  };
  const st = STATUS[fact.status] || STATUS.stated;
  return (
    <li className="py-1.5 border-b border-white/5 last:border-0" data-testid={`trip-fact-${field}`}>
      <div className="flex items-start gap-2">
        <span className="w-[92px] shrink-0 font-mono-wr text-[9px] uppercase tracking-widest text-white/45 pt-[3px]">{LABELS[field][ro ? 1 : 0]}</span>
        {editing ? (
          <div className="flex-1 flex items-center gap-1">
            <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
              className={`flex-1 min-w-0 bg-white/5 border ${bad ? "border-[var(--wr-red)]" : "border-white/15"} rounded-sm px-2 py-0.5 text-[12px] text-white focus:outline-none`}
              data-testid={`trip-fact-${field}-input`} />
            <button onClick={save} aria-label="save" className="p-1 hover:text-white text-white/70" data-testid={`trip-fact-${field}-save`}><Check size={13} /></button>
            <button onClick={() => setEditing(false)} aria-label="cancel" className="p-1 hover:text-white text-white/50"><X size={13} /></button>
          </div>
        ) : (
          <>
            <span className="flex-1 text-[12.5px] text-white/90 leading-snug" data-testid={`trip-fact-${field}-value`}>
              {fmtValue(fact.value)}
              <span className={`ml-1.5 text-[9px] font-mono-wr uppercase tracking-wider ${fact.status === "assumption" ? "text-amber-300/80" : "text-white/35"}`}>· {st[ro ? 1 : 0]}</span>
            </span>
            {fact.status === "assumption" && (
              <button onClick={() => editField(field, null, "confirm")} aria-label="confirm" title={ro ? "Confirmă" : "Confirm"} className="p-1 text-white/45 hover:text-white" data-testid={`trip-fact-${field}-confirm`}><Check size={12} /></button>
            )}
            <button onClick={() => { setVal(fmtValue(fact.value)); setBad(false); setEditing(true); }} aria-label="edit" className="p-1 text-white/45 hover:text-white" data-testid={`trip-fact-${field}-edit`}><Pencil size={12} /></button>
            <button onClick={() => editField(field, null, "delete")} aria-label="remove" className="p-1 text-white/45 hover:text-[var(--wr-red)]" data-testid={`trip-fact-${field}-delete`}><Trash2 size={12} /></button>
          </>
        )}
      </div>
    </li>
  );
}

async function writeClipboard(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch (_) {
    const ta = document.createElement("textarea");
    ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

function CopySummary({ locale }) {
  const { getSummary } = useConcierge();
  const [done, setDone] = useState(false);
  const ro = locale === "ro";
  const copy = async () => {
    const text = await getSummary(locale);
    const ok = text ? await writeClipboard(text) : false;
    if (ok) {
      setDone(true); setTimeout(() => setDone(false), 2500);
      toast.success(ro ? "Rezumatul călătoriei a fost copiat." : "Trip summary copied.");
    } else {
      toast.error(ro ? "Nu am putut copia rezumatul. Încearcă din nou." : "Couldn't copy the summary. Please try again.");
    }
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1.5 text-[11px] text-white/75 hover:text-white" data-testid="trip-copy-summary">
      {done ? <Check size={11} /> : <Copy size={11} />}
      {done ? (ro ? "Copiat" : "Copied") : (ro ? "Copiază rezumatul călătoriei" : "Copy trip summary")}
    </button>
  );
}

export function TripPanel({ locale }) {
  const { profile, clear, messages } = useConcierge();
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const ro = locale === "ro";
  const facts = profile?.facts || {};
  const keys = ORDER.filter((k) => facts[k]);
  if (!keys.length && !messages.length) return null;
  return (
    <div className="border border-white/10 rounded-sm bg-white/[0.03]" data-testid="trip-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 text-left" data-testid="trip-panel-toggle">
        <span className="font-mono-wr text-[10px] uppercase tracking-[0.2em] text-white/70">
          {ro ? "Călătoria ta" : "Your trip"} <span className="text-white/35">· {keys.length}{profile?.itinerary ? ` · ${ro ? "plan" : "plan"} v${profile.itinerary.version}` : ""}</span>
        </span>
        <ChevronDown size={14} className={`text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 max-h-[260px] overflow-y-auto">
          {keys.length ? (
            <ul>{keys.map((k) => <FactRow key={k} field={k} fact={facts[k]} ro={ro} />)}</ul>
          ) : (
            <p className="text-[12px] text-white/50 py-1">{ro ? "Nimic salvat încă." : "Nothing saved yet."}</p>
          )}
          <p className="mt-2 text-[10.5px] text-white/40 leading-snug">
            {ro ? "Păstrat doar pentru această conversație, în acest browser, până la 30 de zile de la ultimul mesaj."
                : "Kept only for this conversation in this browser, up to 30 days after the last message."}
          </p>
          <div className="mt-2"><CopySummary locale={locale} /></div>
          {confirmClear ? (
            <div className="mt-2 flex items-center gap-2 text-[11px]">
              <span className="text-white/70">{ro ? "Ștergi conversația și datele călătoriei?" : "Delete the conversation and trip details?"}</span>
              <button onClick={async () => { await clear(); setConfirmClear(false); setOpen(false); }} className="px-2 py-0.5 bg-[var(--wr-red)] rounded-sm text-white" data-testid="trip-clear-confirm">{ro ? "Șterge" : "Delete"}</button>
              <button onClick={() => setConfirmClear(false)} className="px-2 py-0.5 border border-white/20 rounded-sm text-white/70" data-testid="trip-clear-cancel">{ro ? "Anulează" : "Cancel"}</button>
            </div>
          ) : (
            <button onClick={() => setConfirmClear(true)} className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-white/55 hover:text-[var(--wr-red)]" data-testid="trip-clear">
              <RotateCcw size={11} /> {ro ? "Șterge conversația" : "Clear conversation"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const UNIT = {
  person: ["per person", "de persoană"], person_day: ["per person / day", "de persoană / zi"],
  person_night: ["per person / night", "de persoană / noapte"], room_night: ["per room / night", "pe cameră / noapte"],
  vehicle_day: ["per vehicle / day", "pe vehicul / zi"], group: ["whole group", "tot grupul"], group_day: ["group / day", "grup / zi"],
};
const money = (r, cur) => `${r.min === r.max ? Math.round(r.min) : `${Math.round(r.min)}–${Math.round(r.max)}`} ${cur}`;

export function CostCard({ costs, locale }) {
  const ro = locale === "ro";
  if (!costs?.options?.length) return null;
  const nights = fmtValue(costs.nights), days = fmtValue(costs.days);
  return (
    <div className="mt-3 border border-white/10 rounded-sm p-3 bg-black/20 text-[12px]" data-testid="cost-card">
      <p className="font-mono-wr text-[9px] uppercase tracking-widest text-white/50 mb-2">
        {ro ? `Totaluri calculate · ${costs.people} pers. · ${days} zile · ${nights} nopți` : `Calculated totals · ${costs.people} people · ${days} days · ${nights} nights`}
      </p>
      <div className="space-y-3">
        {costs.options.map((o, i) => (
          <div key={i} data-testid={`cost-option-${i}`}>
            <div className="flex justify-between gap-2 text-white">
              <span className="font-medium">{o.name}</span>
              <span data-testid={`cost-option-${i}-total`}>{money(o.total, o.currency)}</span>
            </div>
            <ul className="mt-1 space-y-0.5 text-white/60">
              {o.lines.map((l, j) => (
                <li key={j} className="flex justify-between gap-2">
                  <span>{l.label}{l.optional ? (ro ? " (opțional)" : " (optional)") : ""} · {Math.round(l.amount_min)}{l.amount_max !== l.amount_min ? `–${Math.round(l.amount_max)}` : ""} {o.currency} {(UNIT[l.unit] || UNIT.group)[ro ? 1 : 0]}</span>
                  <span>{money(l.subtotal, o.currency)}</span>
                </li>
              ))}
            </ul>
            {o.optional_total.max > 0 && (
              <p className="text-white/50 mt-0.5">{ro ? "Opționale în plus:" : "Optional extras:"} {money(o.optional_total, o.currency)}</p>
            )}
            <p className="text-white/45">{ro ? "Per persoană:" : "Per person:"} {money(o.per_person, o.currency)}</p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[10.5px] text-white/40 leading-snug">
        {ro ? "Estimări neverificate, fără prețuri live — verifică înainte de rezervare. Totalurile sunt calculate automat din prețurile unitare de mai sus."
            : "Unverified estimates, no live prices — check before booking. Totals are calculated automatically from the unit prices above."}
        {costs.as_of ? ` ${costs.as_of}` : ""}
      </p>
    </div>
  );
}

export function ChatError({ locale }) {
  const { error, retry, streaming } = useConcierge();
  const ro = locale === "ro";
  if (!error || streaming) return null;
  const expired = error.code === "session_expired" || error.code === "session_gone";
  return (
    <div className="flex items-center gap-2 text-[12px] text-amber-200/90 border border-amber-300/20 bg-amber-300/5 rounded-sm px-3 py-2" data-testid="chat-error">
      <AlertTriangle size={13} />
      <span className="flex-1">
        {expired
          ? (ro ? "Sesiunea a expirat. Trimite din nou mesajul pentru a începe o conversație nouă." : "This session has expired. Send your message again to start a new conversation.")
          : error.code === "unanswered"
            ? (ro ? "Ultimul tău mesaj nu a primit încă răspuns." : "Your last message hasn't been answered yet.")
            : (ro ? "Serviciul nu a putut răspunde acum. Mesajele și datele călătoriei sunt salvate." : "The service couldn't reply just now. Your messages and trip details are saved.")}
      </span>
      {error.retryable && (
        <button onClick={() => retry(locale)} className="px-2 py-0.5 border border-amber-200/40 rounded-sm hover:bg-amber-200/10" data-testid="chat-retry">
          {ro ? "Reîncearcă" : "Retry"}
        </button>
      )}
    </div>
  );
}
